import { MosaicTile, ValidatedFrame, SmoothedFrame } from '../architecture/mosaic_pipeline';

class _CVAxis {
    private readonly R: number;
    private readonly Q: number;

    private pos = NaN;
    private vel = NaN;

    private p00 = NaN; private p01 = NaN; private p10 = NaN; private p11 = NaN;

    constructor(R: number, Q: number) { this.R = R; this.Q = Q; }

    filter(z: number): number {
        if (!isFinite(z) || isNaN(z)) return isNaN(this.pos) ? 0 : this.pos;

        if (isNaN(this.pos)) {
            this.pos = z; this.vel = 0;
            this.p00 = this.Q; this.p01 = 0; this.p10 = 0; this.p11 = this.Q;
            return this.pos;
        }

        const pp  = this.pos + this.vel;
        const vp  = this.vel;
        const pp00 = this.p00 + this.p10 + this.p01 + this.p11 + this.Q;
        const pp01 = this.p01 + this.p11;
        const pp10 = this.p10 + this.p11;
        const pp11 = this.p11 + this.Q;

        if (!isFinite(pp) || !isFinite(pp00)) {
            this.pos = z; this.vel = 0;
            this.p00 = this.Q; this.p01 = 0; this.p10 = 0; this.p11 = this.Q;
            return this.pos;
        }

        const S  = pp00 + this.R;
        const K0 = pp00 / S;
        const K1 = pp10 / S;
        const innov = z - pp;

        this.pos  = pp + K0 * innov;
        this.vel  = vp + K1 * innov;
        this.p00  = pp00 - K0 * pp00;
        this.p01  = pp01 - K0 * pp01;
        this.p10  = pp10 - K1 * pp00;
        this.p11  = pp11 - K1 * pp01;

        return this.pos;
    }
}

export class KalmanFilter2D {
    private readonly _ax: _CVAxis;
    private readonly _ay: _CVAxis;

    constructor(R = 1, Q = 1) {
        this._ax = new _CVAxis(R, Q);
        this._ay = new _CVAxis(R, Q);
    }

    public filter(x: number, y: number): { x: number, y: number } {
        return { x: this._ax.filter(x), y: this._ay.filter(y) };
    }
}

export class KalmanSmootherTile implements MosaicTile<ValidatedFrame, SmoothedFrame> {
  public readonly tileId = 'KalmanSmootherTile';
  private filter: KalmanFilter2D;

  constructor(config: Readonly<{ kalman_q: number; kalman_r: number }>) {
    this.filter = new KalmanFilter2D(config.kalman_r, config.kalman_q);
  }

  public process(frame: ValidatedFrame): SmoothedFrame | null {
    const landmarks = frame.rawLandmarks;
    let x = 0;
    let y = 0;

    if (landmarks && landmarks.length > 8) {
      const lm = landmarks[8];
      if (lm) {
        x = lm.x;
        y = lm.y;
      }
    }

    const smoothed = this.filter.filter(x, y);
    
    return {
      ...frame,
      smoothedX: smoothed.x,
      smoothedY: smoothed.y
    };
  }
}
